/**
 * DMN overlay presentation modes.
 *
 * The DMN editor opens on top of the BPMN canvas in two layouts:
 *   - 'popup' (default): centered modal (~85% × 80%) over a translucent
 *     backdrop. BPMN remains visible behind it.
 *   - 'full': legacy full-viewport overlay.
 *
 * The chosen mode persists in localStorage so the user's preference carries
 * across sessions. Esc, click on the backdrop, and the resize handle are
 * wired here; the host (sfd-v2/src/index.js) supplies the close + layout
 * callbacks because it owns the auto-save flow.
 */

export const DMN_MODE_KEY = 'sfd-v2.dmn-overlay.mode';
export const DEFAULT_MODE = 'popup';
export const MODES = ['popup', 'full'];

const MIN_W = 640;
const MIN_H = 480;

export function getDmnMode() {
  try {
    const m = (typeof localStorage !== 'undefined') ? localStorage.getItem(DMN_MODE_KEY) : null;
    return MODES.includes(m) ? m : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

function persistMode(mode) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(DMN_MODE_KEY, mode);
  } catch { /* noop */ }
}

function syncToggleButtons(mode) {
  document.querySelectorAll('.dmn-overlay-mode-toggle button[data-mode]').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.mode === mode);
  });
}

/**
 * Apply a mode class to the overlay element. Persists, syncs toggle buttons,
 * shows/hides the backdrop, and clears any inline width/height that the
 * resize handle may have set when leaving popup mode.
 */
export function setDmnMode(mode, opts = {}) {
  if (!MODES.includes(mode)) return;
  const overlay = opts.overlayEl || document.getElementById('dmn-overlay');
  const backdrop = opts.backdropEl || document.getElementById('dmn-overlay-backdrop');
  if (overlay) {
    MODES.forEach((m) => overlay.classList.remove(`mode-${m}`));
    overlay.classList.add(`mode-${mode}`);
    if (mode !== 'popup') {
      overlay.style.width = '';
      overlay.style.height = '';
    }
  }
  if (backdrop) {
    const isShown = !!overlay && overlay.classList.contains('show');
    if (mode === 'popup' && isShown) backdrop.classList.add('show');
    else backdrop.classList.remove('show');
  }
  syncToggleButtons(mode);
  persistMode(mode);
  if (typeof opts.onModeChange === 'function') opts.onModeChange(mode);
}

/** Show backdrop when overlay opens (only meaningful in popup mode). */
export function showOverlay({ overlayEl, backdropEl } = {}) {
  const overlay = overlayEl || document.getElementById('dmn-overlay');
  const backdrop = backdropEl || document.getElementById('dmn-overlay-backdrop');
  if (!overlay) return;
  overlay.classList.add('show');
  if (backdrop && overlay.classList.contains('mode-popup')) {
    backdrop.classList.add('show');
  }
}

/** Hide overlay + backdrop. */
export function hideOverlay({ overlayEl, backdropEl } = {}) {
  const overlay = overlayEl || document.getElementById('dmn-overlay');
  const backdrop = backdropEl || document.getElementById('dmn-overlay-backdrop');
  overlay?.classList.remove('show');
  backdrop?.classList.remove('show');
}

/**
 * Wire mode toggle buttons, Esc-to-close, click-on-backdrop-to-close, and
 * the resize handle. Returns a teardown function for tests.
 *
 * Options:
 *   overlayEl, backdropEl   — explicit DOM nodes (else looked up by id)
 *   onClose                 — invoked on Esc / backdrop click
 *   onResize                — invoked while dragging the resize handle
 *   onModeChange            — invoked after every mode change
 */
export function mountDmnOverlayModes(options = {}) {
  const overlay = options.overlayEl || document.getElementById('dmn-overlay');
  const backdrop = options.backdropEl || document.getElementById('dmn-overlay-backdrop');
  if (!overlay) return () => {};

  setDmnMode(getDmnMode(), { overlayEl: overlay, backdropEl: backdrop, onModeChange: options.onModeChange });

  const onToggleClick = (e) => {
    const btn = e.target.closest('button[data-mode]');
    if (!btn) return;
    setDmnMode(btn.dataset.mode, { overlayEl: overlay, backdropEl: backdrop, onModeChange: options.onModeChange });
    if (typeof options.onResize === 'function') options.onResize();
  };
  const toggleEl = overlay.querySelector('.dmn-overlay-mode-toggle');
  toggleEl?.addEventListener('click', onToggleClick);

  const onKeydown = (e) => {
    if (e.key !== 'Escape') return;
    if (!overlay.classList.contains('show')) return;
    e.preventDefault();
    if (typeof options.onClose === 'function') options.onClose();
  };
  document.addEventListener('keydown', onKeydown);

  const onBackdropClick = () => {
    if (!overlay.classList.contains('show')) return;
    if (!overlay.classList.contains('mode-popup')) return;
    if (typeof options.onClose === 'function') options.onClose();
  };
  backdrop?.addEventListener('click', onBackdropClick);

  const handle = overlay.querySelector('.dmn-overlay-resize');
  let onMove = null, onUp = null;
  const onMouseDown = (ev) => {
    if (!overlay.classList.contains('mode-popup')) return;
    ev.preventDefault();
    const startX = ev.clientX;
    const startY = ev.clientY;
    const rect = overlay.getBoundingClientRect();
    const startW = rect.width;
    const startH = rect.height;
    onMove = (e) => {
      const w = Math.max(MIN_W, startW + (e.clientX - startX));
      const h = Math.max(MIN_H, startH + (e.clientY - startY));
      overlay.style.width = `${w}px`;
      overlay.style.height = `${h}px`;
      if (typeof options.onResize === 'function') options.onResize();
    };
    onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      onMove = null;
      onUp = null;
      if (typeof options.onResize === 'function') options.onResize();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  handle?.addEventListener('mousedown', onMouseDown);

  return function teardown() {
    toggleEl?.removeEventListener('click', onToggleClick);
    document.removeEventListener('keydown', onKeydown);
    backdrop?.removeEventListener('click', onBackdropClick);
    handle?.removeEventListener('mousedown', onMouseDown);
    if (onMove) document.removeEventListener('mousemove', onMove);
    if (onUp) document.removeEventListener('mouseup', onUp);
  };
}
